'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CaretLeft,
  Users,
  Plus,
  Copy,
  Crown,
  User,
  X,
  Spinner,
  Envelope,
  DotsThree,
  Shield,
  PencilSimple,
  Warning,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Dialog } from '../components/ui/Dialog';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { useToastActions } from '../components/ui/Toast';
import { teamApi } from '../api/team';
import type { Team, TeamMember, TeamInvitation } from '../types/team';
import { useTeamStore } from '../stores/teamStore';
import { useAuthStore } from '../stores/authStore';
import { SSOConfigPanel } from '../components/settings/SSOConfigPanel';

export function TeamDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { teamId } = params;
  const { user } = useAuthStore();
  const { success: showSuccess, error: showError } = useToastActions();
  const { setCurrentTeamId } = useTeamStore();
  
  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // 邀请对话框状态
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  
  // 标签页状态
  const [activeTab, setActiveTab] = useState<'members' | 'invitations' | 'sso'>('members');
  
  // 编辑团队状态
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  
  // 离开团队对话框
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  // 当前用户在该团队的角色
  const [currentUserRole, setCurrentUserRole] = useState<'owner' | 'admin' | 'member'>('member');

  // 检查是否是管理员或所有者
  const isAdminOrOwner = currentUserRole === 'owner' || currentUserRole === 'admin';
  // 检查是否是所有者
  const isOwner = currentUserRole === 'owner';

  // 当用户角色确定为普通成员时，如果当前在 invitations 或 sso 标签页，切换到 members
  useEffect(() => {
    if (!isAdminOrOwner && (activeTab === 'invitations' || activeTab === 'sso')) {
      setActiveTab('members');
    }
  }, [isAdminOrOwner, activeTab]);

  // 获取团队数据
  const fetchTeamData = async () => {
    if (!teamId) return;
    
    try {
      setIsLoading(true);
      
      // 先获取团队和成员信息
      const [teamRes, membersRes] = await Promise.all([
        teamApi.getTeamById(teamId),
        teamApi.getTeamMembers(teamId),
      ]);
      
      if (teamRes.code === 0 && teamRes.data) {
        setTeam(teamRes.data);
      }
      
      const membersList = membersRes.data?.members || [];
      setMembers(membersList);
      
      // 确定当前用户的角色
      let userRole: 'owner' | 'admin' | 'member' = 'member';
      if (user) {
        const currentMember = membersList.find(m => m.userId === user.id);
        if (currentMember) {
          userRole = currentMember.role as 'owner' | 'admin' | 'member';
          setCurrentUserRole(userRole);
        }
      }
      
      // 只有管理员或所有者才获取邀请列表
      if (userRole === 'owner' || userRole === 'admin') {
        try {
          const invitationsRes = await teamApi.getPendingInvitations(teamId);
          setInvitations(invitationsRes.data?.invitations || []);
        } catch (inviteError) {
          // 获取邀请列表失败不影响页面显示
          console.warn('Failed to fetch invitations:', inviteError);
          setInvitations([]);
        }
      } else {
        setInvitations([]);
      }
    } catch (error) {
      showError(t('team.fetchError'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamData();
  }, [teamId, user]);

  // 生成邀请链接
  const handleGenerateLink = useCallback(async (role: 'admin' | 'member') => {
    if (!teamId) return;
    try {
      setIsGeneratingLink(true);
      const response = await teamApi.generateInviteLink(role, teamId);
      // 从vite配置中获取base domain，将其和inviteUrl拼接成完整邀请链接
      const domain = import.meta.env.VITE_APP_DOMAIN || window.location.origin;
      let invitePath = response.data?.inviteUrl || '';
      // 如果inviteUrl本身已经是绝对路径/完整url，不重复拼接
      const inviteUrlIsAbsolute = /^https?:\/\//.test(invitePath);
      const fullInviteUrl = inviteUrlIsAbsolute ? invitePath : domain.replace(/\/$/, '') + invitePath;
      setInviteLink(fullInviteUrl ?? null);
    } catch (error) {
      showError(t('team.generateLinkError'));
    } finally {
      setIsGeneratingLink(false);
    }
  }, [teamId, showError, t]);

  // 追踪上一次生成的角色和对话框状态
  const prevRoleRef = useRef<'admin' | 'member'>('member');
  const wasDialogOpenRef = useRef(false);

  // 对话框打开时或角色改变时生成链接
  useEffect(() => {
    if (isInviteDialogOpen && teamId && isAdminOrOwner) {
      // 对话框刚打开（之前是关闭的）或者角色变化时生成链接
      const dialogJustOpened = !wasDialogOpenRef.current;
      const roleChanged = prevRoleRef.current !== inviteRole;
      
      if (dialogJustOpened || roleChanged) {
        prevRoleRef.current = inviteRole;
        handleGenerateLink(inviteRole);
      }
    }
    // 更新对话框状态引用
    wasDialogOpenRef.current = isInviteDialogOpen;
  }, [isInviteDialogOpen, teamId, inviteRole, isAdminOrOwner, handleGenerateLink]);

  // 复制邀请链接
  const handleCopyLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      showSuccess(t('team.linkCopied'));
    }
  };

  // 邀请成员
  const handleInvite = async () => {
    if (!inviteEmail.trim() || !teamId) {
      showError(t('team.emailRequired'));
      return;
    }

    try {
      setIsInviting(true);
      await teamApi.inviteByEmail(inviteEmail, inviteRole, teamId);
      showSuccess(t('team.inviteSuccess'));
      setInviteEmail('');
      setIsInviteDialogOpen(false);
      fetchTeamData();
    } catch (error) {
      showError(t('team.inviteError'));
    } finally {
      setIsInviting(false);
    }
  };

  // 移除成员
  const handleRemoveMember = async (memberId: string) => {
    if (!confirm(t('team.confirmRemove'))) return;
    if (!teamId) return;

    try {
      await teamApi.removeMember(memberId, teamId);
      showSuccess(t('team.removeSuccess'));
      fetchTeamData();
    } catch (error) {
      showError(t('team.removeError'));
    }
  };

  // 更新成员角色
  const handleUpdateRole = async (memberId: string, newRole: 'admin' | 'member') => {
    if (!teamId) return;
    
    try {
      await teamApi.updateMemberRole(memberId, newRole, teamId);
      showSuccess(t('team.updateRoleSuccess'));
      fetchTeamData();
    } catch (error) {
      showError(t('team.updateRoleError'));
    }
  };

  // 取消邀请
  const handleCancelInvitation = async (invitationId: string) => {
    if (!teamId) return;
    
    try {
      await teamApi.cancelInvitation(invitationId, teamId);
      showSuccess(t('team.cancelInviteSuccess'));
      fetchTeamData();
    } catch (error) {
      showError(t('team.cancelInviteError'));
    }
  };

  // 保存团队名称
  const handleSaveTeamName = async () => {
    if (!teamId || !editName.trim()) return;

    try {
      setIsEditing(true);
      await teamApi.updateTeam(teamId, { name: editName.trim() });
      showSuccess(t('team.updateSuccess'));
      setTeam({ ...team!, name: editName.trim() });
      setIsEditDialogOpen(false);
    } catch (error) {
      showError(t('team.updateError'));
    } finally {
      setIsEditing(false);
    }
  };

  // 离开团队
  const handleLeaveTeam = async () => {
    if (!teamId) return;
    
    try {
      setIsLeaving(true);
      await teamApi.leaveTeam(teamId);
      showSuccess(t('team.leaveSuccess') || '已离开团队');
      setCurrentTeamId('');
      navigate('/team');
    } catch (error) {
      showError(t('team.leaveError') || '离开团队失败');
    } finally {
      setIsLeaving(false);
      setIsLeaveDialogOpen(false);
    }
  };

  // 打开编辑对话框
  const handleOpenEditDialog = () => {
    if (team) {
      setEditName(team.name);
      setIsEditDialogOpen(true);
    }
  };

  // 获取角色图标
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-4 h-4 text-amber-500" weight="fill" />;
      case 'admin':
        return <Shield className="w-4 h-4 text-blue-500" weight="fill" />;
      default:
        return <User className="w-4 h-4 text-zinc-400" weight="fill" />;
    }
  };

  // 获取角色标签
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return t('team.roleOwner');
      case 'admin':
        return t('team.roleAdmin');
      default:
        return t('team.roleMember');
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!team) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="h-full bg-zinc-50 overflow-auto"
      >
        <div className="max-w-3xl mx-auto p-6">
          <button
            onClick={() => navigate('/team')}
            className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 transition-colors mb-8"
          >
            <CaretLeft className="w-4 h-4" />
            {t('common.back')}
          </button>
          <div className="bg-white rounded-2xl border border-zinc-200/50 p-12 text-center">
            <Users className="w-14 h-14 text-zinc-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-zinc-900 mb-2">
              {t('team.notFound') || '团队不存在'}
            </h2>
            <p className="text-sm text-zinc-500 mb-6">
              {t('team.notFoundDesc') || '该团队可能已被删除或您没有访问权限'}
            </p>
            <Button onClick={() => navigate('/team')}>
              {t('team.backToList') || '返回团队列表'}
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ type: 'spring', stiffness: 100, damping: 20 }}
      className="h-full bg-zinc-50 overflow-auto"
    >
      <div className="max-w-3xl mx-auto p-6">
        {/* 返回按钮和标题 */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/team')}
            className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 transition-colors mb-4"
          >
            <CaretLeft className="w-4 h-4" />
            {t('team.backToList') || '返回团队列表'}
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-zinc-900">{team.name}</h1>
              {isOwner && (
                <button
                  onClick={handleOpenEditDialog}
                  className="p-1 hover:bg-zinc-200 rounded-md transition-colors"
                  title={t('team.editTeam') || '编辑团队'}
                >
                  <PencilSimple className="w-4 h-4 text-zinc-400" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* 成员角色标签 */}
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-100 text-xs font-medium text-zinc-600">
                {getRoleIcon(currentUserRole)}
                {getRoleLabel(currentUserRole)}
              </div>
              {/* 邀请按钮 - 仅管理员显示 */}
              {isAdminOrOwner && activeTab !== 'sso' && (
                <Button
                  onClick={() => setIsInviteDialogOpen(true)}
                  className="flex items-center gap-2"
                  size="sm"
                >
                  <Plus className="w-4 h-4" />
                  {t('team.inviteMember')}
                </Button>
              )}
              {/* 离开团队按钮 - 非所有者显示 */}
              {!isOwner && (
                <Button
                  variant="secondary"
                  onClick={() => setIsLeaveDialogOpen(true)}
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                >
                  {t('team.leaveTeam') || '离开团队'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* 标签切换 */}
        <div className="flex gap-1 mb-6 bg-zinc-100 p-1 rounded-xl w-fit">
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              activeTab === 'members'
                ? 'bg-white text-zinc-900 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            {t('team.members')} ({members.length})
          </button>
          {/* 邀请列表 - 仅管理员可见 */}
          {/* {isAdminOrOwner && (
            <button
              onClick={() => setActiveTab('invitations')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                activeTab === 'invitations'
                  ? 'bg-white text-zinc-900 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              {t('team.pendingInvitations')} ({invitations.length})
            </button>
          )} */}
          {/* SSO配置 - 仅管理员可见 */}
          {/* {isAdminOrOwner && (
            <button
              onClick={() => setActiveTab('sso')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                activeTab === 'sso'
                  ? 'bg-white text-zinc-900 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              <Key className="w-4 h-4" />
              {t('settings.sso.title') || 'SSO'}
            </button>
          )}*/}
        </div> 

        {/* 成员列表 */}
        {activeTab === 'members' && (
          <div className="bg-white rounded-2xl border border-zinc-200/50 overflow-hidden">
            {members.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <p className="text-zinc-500">{t('team.noMembers')}</p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-medium">
                        {member.userName?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-900">
                          {member.userName || t('team.unknownUser')}
                          {member.userId === user?.id && (
                            <span className="ml-2 text-xs text-zinc-400">({t('team.you') || '我'})</span>
                          )}
                        </p>
                        <p className="text-xs text-zinc-500">{member?.userEmail}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-100 text-xs font-medium text-zinc-600">
                        {getRoleIcon(member.role)}
                        {getRoleLabel(member.role)}
                      </div>
                      {/* 管理操作 - 仅对所有者和管理员显示，且不能操作所有者 */}
                      {isAdminOrOwner && member.role !== 'owner' && member.userId !== user?.id && (
                        <DropdownMenu
                          trigger={
                            <button className="p-2 hover:bg-zinc-100 rounded-lg transition-colors">
                              <DotsThree className="w-5 h-5 text-zinc-400" />
                            </button>
                          }
                          items={[
                            // 只有所有者可以更改管理员角色
                            ...(isOwner && member.role === 'admin'
                              ? [
                                  {
                                    label: t('team.setAsMember'),
                                    onClick: () => handleUpdateRole(member.id, 'member'),
                                  },
                                ]
                              : []),
                            ...(isOwner && member.role === 'member'
                              ? [
                                  {
                                    label: t('team.setAsAdmin'),
                                    onClick: () => handleUpdateRole(member.id, 'admin'),
                                  },
                                ]
                              : []),
                            {
                              label: t('team.removeMember'),
                              onClick: () => handleRemoveMember(member.id),
                              danger: true,
                            },
                          ]}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 邀请列表 */}
        {activeTab === 'invitations' && isAdminOrOwner && (
          <div className="bg-white rounded-2xl border border-zinc-200/50 overflow-hidden">
            {invitations.length === 0 ? (
              <div className="p-12 text-center">
                <Envelope className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <p className="text-zinc-500">{t('team.noInvitations')}</p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {invitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center">
                        <Envelope className="w-5 h-5 text-zinc-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-900">{invitation.email}</p>
                        <p className="text-xs text-zinc-500">
                          {t('team.invitedAs', { role: getRoleLabel(invitation.role) })}
                          {' · '}
                          {new Date(invitation.expiresAt).toLocaleDateString()}
                          {t('team.expires')}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      className="p-2 hover:bg-red-50 text-zinc-400 hover:text-red-500 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SSO 配置 */}
        {activeTab === 'sso' && isAdminOrOwner && teamId && (
          <div className="bg-white rounded-2xl border border-zinc-200/50 p-6">
            <SSOConfigPanel 
              teamId={teamId} 
              canManage={isAdminOrOwner} 
            />
          </div>
        )}
      </div>

      {/* 邀请对话框 - 仅管理员可见 */}
      {isAdminOrOwner && (
        <Dialog
          isOpen={isInviteDialogOpen}
          onClose={() => {
            setIsInviteDialogOpen(false);
            setInviteEmail('');
            setInviteLink(null);
          }}
          title={t('team.inviteMember')}
        >
          <div className="space-y-4">
            {/* 邮箱邀请 */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                {t('team.emailInvite')}
              </label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder={t('team.emailPlaceholder')}
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="flex-1"
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
                  className="px-3 py-2 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="member">{t('team.roleMember')}</option>
                  <option value="admin">{t('team.roleAdmin')}</option>
                </select>
              </div>
              <Button
                onClick={handleInvite}
                disabled={isInviting || !inviteEmail.trim()}
                className="w-full mt-2"
              >
                {isInviting ? (
                  <Spinner className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Envelope className="w-4 h-4 mr-2" />
                    {t('team.sendInvite')}
                  </>
                )}
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-zinc-500">{t('common.or')}</span>
              </div>
            </div>

            {/* 链接邀请 */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                {t('team.linkInvite')}
              </label>
              <div className="flex gap-2">
                <Input
                  value={inviteLink || ''}
                  readOnly
                  placeholder={isGeneratingLink ? t('common.loading') : ''}
                  className="flex-1 text-sm"
                />
                <Button 
                  variant="secondary" 
                  onClick={handleCopyLink}
                  disabled={!inviteLink || isGeneratingLink}
                  title={t('team.copyLink')}
                >
                  {isGeneratingLink ? (
                    <Spinner className="w-4 h-4 animate-spin" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                {t('team.linkInviteDesc') || '复制链接发送给成员，他们可以通过此链接加入团队'}
              </p>
            </div>
          </div>
        </Dialog>
      )}

      {/* 编辑团队对话框 - 仅所有者 */}
      {isOwner && (
        <Dialog
          isOpen={isEditDialogOpen}
          onClose={() => { 
            setIsEditDialogOpen(false); 
            setEditName(''); 
          }}
          title={t('team.editTeam') || '编辑团队'}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                {t('team.teamName') || '团队名称'}
              </label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={t('team.teamNamePlaceholder') || '输入团队名称'}
                className="w-full"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setIsEditDialogOpen(false)}
                className="flex-1"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSaveTeamName}
                disabled={isEditing || !editName.trim()}
                className="flex-1"
              >
                {isEditing ? <Spinner className="w-4 h-4 animate-spin" /> : (t('common.save') || '保存')}
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      {/* 离开团队确认对话框 */}
      <Dialog
        isOpen={isLeaveDialogOpen}
        onClose={() => setIsLeaveDialogOpen(false)}
        title={t('team.leaveTeamConfirm') || '确认离开团队'}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 text-amber-600 bg-amber-50 p-4 rounded-xl">
            <Warning className="w-5 h-5 flex-shrink-0 mt-0.5" weight="fill" />
            <p className="text-sm">
              {t('team.leaveTeamWarning') || '离开团队后，您将失去访问该团队资源的权限。此操作无法撤销。'}
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setIsLeaveDialogOpen(false)}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleLeaveTeam}
              disabled={isLeaving}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              {isLeaving ? <Spinner className="w-4 h-4 animate-spin" /> : (t('team.confirmLeave') || '确认离开')}
            </Button>
          </div>
        </div>
      </Dialog>
    </motion.div>
  );
}
